import { useAccount, useMsal } from '@azure/msal-react';
import './App.css';
import Layout from './components/layout/Layout';
import Routing from './components/routes/Routing';
import { translationService } from 'e.sic-platform.webclient/src/services/translation/TranslationService';
import LoadingSpinner from 'e.sic-platform.webclient/src/components/loading-spinner/LoadingSpinner';
import { observer } from 'mobx-react-lite';
import useUrlQueryParams from 'e.sic-platform.webclient/src/services/url/UrlQueryService';
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { appSessionStorage } from 'e.sic-platform.webclient/src/services/storage/SessionStorageService';
import { authentication, POST_LOGIN_ROUTE_KEY } from 'e.sic-platform.webclient/src/services/authentication/Authentication';
import { useNavigate } from 'react-router-dom';
import { VIEWER_TOKEN_KEY } from 'e.sic-platform.webclient/src/services/authentication/AuthService';
import { areLoading } from 'e.sic-platform.webclient/src/services/storage/StorageModel';
import { lastAccessedOrganizationService } from 'e.sic-platform.webclient/src/services/organization/LastAccessedOrganizationStorage';
import { organizationSubscriptionService } from 'e.sic-platform.webclient/src/services/organization/OrganizationSubscription';
import { permissionsService } from 'e.sic-platform.webclient/src/services/authorization/UserPermissionsService';
import { currentUserService } from 'e.sic-platform.webclient/src/services/user/CurrentUserStorage';
import { featureFlagsStorage } from 'e.sic-platform.webclient/src/services/feature-flags/FeatureFlagsStorage';
import { activityPricesStorage } from 'e.sic-platform.webclient/src/services/shop/ActivityPricesStorage';
import { creditBalanceStorage } from 'e.sic-platform.webclient/src/services/shop/CreditBalanceStorage';
import { shopHistoryStorage } from 'e.sic-platform.webclient/src/services/shop/ShopHistoryStorage';
import { mainCodeColorStorage } from 'e.sic-platform.webclient/src/services/mc-colors/MainCodeColorStorage';

// Guests only wait on the globally-available home-page data (feature flags + prices). The
// auth-only stores join the gate only once authenticated — otherwise they hang forever without
// an org/user and a guest would be stuck on the spinner.
//
// Declared at MODULE scope, not rebuilt per render: `areLoading` is a `computedFn` keyed on its
// argument, so passing a freshly built array every render meant the cache never hit and a new
// computed was created on each pass. The labels are shown by the boot-failure panel below, so a
// stalled store is named instead of leaving the user staring at a spinner.
const ALWAYS_NEEDED = [
  { labelKey: 'general.boot.store.featureFlags', store: featureFlagsStorage },
  { labelKey: 'general.boot.store.prices', store: activityPricesStorage },
];
const AUTH_ONLY = [
  { labelKey: 'general.boot.store.organization', store: lastAccessedOrganizationService },
  { labelKey: 'general.boot.store.subscription', store: organizationSubscriptionService },
  { labelKey: 'general.boot.store.permissions', store: permissionsService },
  { labelKey: 'general.boot.store.user', store: currentUserService },
  { labelKey: 'general.boot.store.credits', store: creditBalanceStorage },
  { labelKey: 'general.boot.store.history', store: shopHistoryStorage },
  { labelKey: 'general.boot.store.codeColors', store: mainCodeColorStorage },
];
const GUEST_GATE = ALWAYS_NEEDED.map((s) => s.store);
const SIGNED_IN_GATE = [...ALWAYS_NEEDED, ...AUTH_ONLY].map((s) => s.store);

// How long to wait before admitting the boot is not going to finish. Generous enough for a
// cold API on a slow connection, short enough that nobody sits on a dead splash.
const BOOT_TIMEOUT_MS = 15000;

export default observer(function App() {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { authToken } = useUrlQueryParams();
  const navigate = useNavigate();

  // Public home page: resolve auth state WITHOUT ever forcing a login. A valid cached
  // session logs in silently; a stale/dead account or no account → guest (resolveSession).
  useEffect(() => {
    const viewerAuthToken = authToken || appSessionStorage.getStorageItem(VIEWER_TOKEN_KEY);
    authentication.resolveSession(instance, account, viewerAuthToken, inProgress);
  }, [account, authToken, inProgress, instance]);

  // After a login triggered by opening an app while signed out, land on that app's route.
  // MSAL's redirectStartPage usually already does this; this is a defensive fallback.
  useEffect(() => {
    if (authentication.anonymous) return;
    const target = appSessionStorage.getStorageItem(POST_LOGIN_ROUTE_KEY);
    if (!target) return;
    appSessionStorage.removeStorageItem(POST_LOGIN_ROUTE_KEY);
    if (window.location.pathname + window.location.search !== target) navigate(target);
  }, [authentication.anonymous, navigate]);

  const gatedStores = authentication.anonymous ? ALWAYS_NEEDED : [...ALWAYS_NEEDED, ...AUTH_ONLY];
  const stillLoading =
    areLoading(authentication.anonymous ? GUEST_GATE : SIGNED_IN_GATE) ||
    !translationService.translationsLoaded;

  // Give up on the splash rather than spinning indefinitely. A store that finishes with no data
  // now reports "not loading" (see StorageModel.loadAttempted), but a request that simply never
  // settles still would — so bound the wait and say what is stuck.
  const [bootTimedOut, setBootTimedOut] = useState(false);
  useEffect(() => {
    if (!stillLoading) {
      setBootTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setBootTimedOut(true), BOOT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [stillLoading]);

  const retryBoot = useCallback(() => {
    setBootTimedOut(false);
    (authentication.anonymous ? GUEST_GATE : SIGNED_IN_GATE).forEach((store: any) => {
      if (store?.isLoading) void store.load?.();
    });
    if (!translationService.translationsLoaded) window.location.reload();
  }, []);

  // What the user has been doing by hand in DevTools: drop the cached MSAL session. A stale
  // account is what makes the app resolve as signed-in and pull the org-chain stores into the
  // gate in the first place.
  const resetSession = useCallback(async () => {
    await authentication.resetLocalSession();
    window.location.reload();
  }, []);

  if (stillLoading && !bootTimedOut) {
    return <LoadingSpinner fullPage />;
  }

  if (stillLoading && bootTimedOut) {
    const unresolved = gatedStores.filter(({ store }) => (store as any).isLoading);
    // One reason this panel exists is a FAILED translation load, and translate() returns "" in
    // that state — so every string here needs a literal fallback or the panel renders blank.
    const text = (key: string, fallback: string) => translationService.translate(key) || fallback;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', p: 3 }}>
        <Paper elevation={4} sx={{ p: 4, maxWidth: 560 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {text('general.boot.failed.title', 'The application could not finish loading')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {text(
              'general.boot.failed.message',
              'Some data could not be loaded. Retry, or reset the stored session if this keeps happening.'
            )}
          </Typography>
          {unresolved.length > 0 && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {text('general.boot.failed.pending', 'Still waiting on:')}{' '}
              {unresolved
                .map(({ labelKey }) => text(labelKey, labelKey.split('.').pop() ?? labelKey))
                .join(', ')}
            </Typography>
          )}
          {!translationService.translationsLoaded && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {text('general.boot.store.translations', 'Translations')}
            </Typography>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={retryBoot}>
              {text('general.boot.failed.retry', 'Retry')}
            </Button>
            <Button variant="outlined" color="warning" onClick={resetSession}>
              {text('general.boot.failed.resetSession', 'Reset session')}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Layout>
      <Routing />
    </Layout>
  );
})
